import React, { useState, useEffect } from "react";

// SVG Check Mark Component
const CheckMarkSVG = ({ size = 16, color = "#374151" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const QuizSession00 = ({ 
  showAnswers, 
  reviewMode = false, 
  onComplete, 
  resetQuiz, 
  requireCorrectAnswer = false 
}) => {
  const [score, setScore] = useState(0);
  const [timeTaken, setTimeTaken] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (resetQuiz) {
      console.log("🔄 Resetting quiz state");
      setQuizCompleted(false);
      setUserAnswers([]);
      setCurrentQuestionIndex(0);
      setStartTime(Date.now());
      setIsFinished(false);
      setScore(0);
      setTimeTaken(0);
      setFeedback("");
    }
  }, [resetQuiz]);

  const questions = [
    {
      question: "What is the main goal of the Face Forward program?",
      options: [
        "To eliminate all pain completely",
        "To learn skills to manage orofacial pain and improve quality of life",
        "To replace all medical treatments",
        "To diagnose the cause of your pain"
      ],
      correctAnswer: "To learn skills to manage orofacial pain and improve quality of life",
      explanation: "Face Forward is designed to help you develop practical skills and strategies to better manage your orofacial pain symptoms and improve your overall quality of life. The goal is not to eliminate pain entirely, but to help you live well despite pain.",
      explanations: {
        "To eliminate all pain completely": "Try again. While we hope to reduce pain, the program focuses on managing pain and improving quality of life rather than promising complete elimination.",
        "To learn skills to manage orofacial pain and improve quality of life": "Correct! Face Forward teaches evidence-based skills to help you manage your pain and live a fuller life.",
        "To replace all medical treatments": "Try again. This program complements, not replaces, your medical care.",
        "To diagnose the cause of your pain": "Try again. This program is about management strategies, not diagnosis."
      }
    },
    {
      question: "How many main sessions are included in the Face Forward program?",
      options: [
        "3 sessions",
        "5 sessions",
        "7 sessions",
        "10 sessions"
      ],
      correctAnswer: "5 sessions",
      explanation: "The Face Forward program consists of 5 comprehensive sessions, each focusing on different aspects of pain management including understanding chronic pain, emotional challenges, negative thoughts, acceptance, and building resilience.",
      explanations: {
        "3 sessions": "Try again. The program has more sessions to cover all the important topics.",
        "5 sessions": "Correct! Face Forward includes 5 structured sessions covering key pain management skills.",
        "7 sessions": "Try again. Count again - there are fewer main sessions.",
        "10 sessions": "Try again. The program is more focused with fewer sessions."
      }
    },
    {
      question: "What type of approach does Face Forward use?",
      options: [
        "Only medication management",
        "Surgery-focused treatment",
        "Evidence-based techniques from cognitive behavioral therapy and mindfulness",
        "Alternative medicine only"
      ],
      correctAnswer: "Evidence-based techniques from cognitive behavioral therapy and mindfulness",
      explanation: "Face Forward uses proven, evidence-based techniques drawn from cognitive behavioral therapy (CBT) and mindfulness practices. These approaches have been scientifically validated to help people manage chronic pain effectively.",
      explanations: {
        "Only medication management": "Try again. This program uses psychological and behavioral techniques, not medication management.",
        "Surgery-focused treatment": "Try again. This program focuses on skills-based approaches, not surgical interventions.",
        "Evidence-based techniques from cognitive behavioral therapy and mindfulness": "Correct! The program uses scientifically proven methods from CBT and mindfulness to help manage pain.",
        "Alternative medicine only": "Try again. The program uses evidence-based psychological approaches that complement traditional medicine."
      }
    }
  ];

  const handleFinishQuiz = async () => {
    if (reviewMode) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    console.log('🔍 Starting quiz submission for Introduction...');
    setIsSaving(true);

    const endTime = Date.now();
    const timeTakenInSeconds = (endTime - startTime) / 1000;

    let calculatedScore = 0;
    questions.forEach((question, index) => {
      if (userAnswers[index] === question.correctAnswer) {
        calculatedScore += 1;
      }
    });

    setScore(calculatedScore);
    setTimeTaken(Math.floor(timeTakenInSeconds));
    setIsFinished(true);
    setQuizCompleted(true);

    const quizData = {
      score: calculatedScore,
      answers: userAnswers,
      attempts: 1,
      totalQuestions: questions.length,
      timeTaken: Math.floor(timeTakenInSeconds)
    };

    console.log('✅ Quiz completed:', quizData);

    if (onComplete) {
      console.log("✅ Quiz finished - calling onComplete callback");
      onComplete(quizData);
    }

    setIsSaving(false);
  };

  const handleAnswerSelection = (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const updatedAnswers = [...userAnswers];
    updatedAnswers[currentQuestionIndex] = answer;
    setUserAnswers(updatedAnswers);
    
    setFeedback(currentQuestion.explanations[answer]);
  };

  const handleNextQuestion = () => {
    if (requireCorrectAnswer && !reviewMode) {
      const currentQuestion = questions[currentQuestionIndex];
      const selectedAnswer = userAnswers[currentQuestionIndex];
      
      if (!selectedAnswer || selectedAnswer !== currentQuestion.correctAnswer) {
        return;
      }
    }

    setFeedback("");
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    setFeedback("");
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = userAnswers[currentQuestionIndex];
  const isCorrectAnswer = selectedAnswer === currentQuestion.correctAnswer;

  const canProceed = reviewMode || 
                    !requireCorrectAnswer || 
                    (selectedAnswer && isCorrectAnswer);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isFinished || reviewMode ? (
        <div>
          <div style={{
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              backgroundColor: '#F8F9FA',
              padding: '4px 8px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              display: 'inline-block'
            }}>
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 20px 0',
              lineHeight: '1.4',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
              {currentQuestion.question}
            </h3>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '24px'
          }}>
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrect = option === currentQuestion.correctAnswer;
              
              let buttonColor = '#374151';
              let backgroundColor = '#FFFFFF';
              let borderColor = '#E5E7EB';
              let showCheckmark = false;

              if (reviewMode) {
                if (isCorrect) {
                  backgroundColor = '#ECFDF5';
                  borderColor = '#10B981';
                  buttonColor = '#374151';
                  showCheckmark = true;
                } else {
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              } else {
                if (isSelected) {
                  if (feedback) {
                    if (isCorrect) {
                      backgroundColor = '#ECFDF5';
                      borderColor = '#10B981';
                      buttonColor = '#374151';
                      showCheckmark = true;
                    } else {
                      backgroundColor = '#F8F9FA';
                      borderColor = '#D1D5DB';
                      buttonColor = '#374151';
                    }
                  } else {
                    backgroundColor = '#EFF6FF';
                    borderColor = '#3B82F6';
                    buttonColor = '#1B365D';
                  }
                } else {
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => !reviewMode && handleAnswerSelection(option)}
                  disabled={reviewMode}
                  style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '1.4',
                    minHeight: '60px',
                    color: buttonColor,
                    backgroundColor: backgroundColor,
                    border: `2px solid ${borderColor}`,
                    cursor: reviewMode ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    userSelect: 'none',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#F8F9FA';
                      e.target.style.borderColor = '#D1D5DB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#FFFFFF';
                      e.target.style.borderColor = '#E5E7EB';
                    }
                  }}
                >
                  {showCheckmark && (
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <CheckMarkSVG size={10} color="#FFFFFF" />
                    </div>
                  )}
                  <span style={{ flex: 1 }}>
                    {option}
                  </span>
                </button>
              );
            })}
          </div>

          {reviewMode && currentQuestion.explanation && (
            <div style={{
              padding: '16px',
              backgroundColor: '#F0F9FF',
              borderRadius: '12px',
              border: '1px solid #E0E7FF',
              marginBottom: '24px'
            }}>
              <div style={{ 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151',
                fontSize: '14px'
              }}>
                Why this is correct:
              </div>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4'
              }}>
                {currentQuestion.explanation}
              </div>
            </div>
          )}

          {feedback && !reviewMode && (
            <div style={{
              padding: '16px',
              backgroundColor: feedback.includes("Correct") ? "#ECFDF5" : "#DBEAFE",
              borderRadius: '12px',
              border: feedback.includes("Correct") ? '1px solid #10B981' : '1px solid #60A5FA',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4',
                fontWeight: '500'
              }}>
                {feedback}
              </div>
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              style={{
                backgroundColor: currentQuestionIndex === 0 ? '#F8F9FA' : '#FFFFFF',
                color: '#374151',
                border: '1px solid #E5E7EB',
                borderRadius: '24px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                boxShadow: currentQuestionIndex === 0 ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              Previous
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {questions.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: index === currentQuestionIndex ? '#1B365D' :
                      userAnswers[index] ? '#10B981' : '#E5E7EB',
                    border: '1px solid #E5E7EB',
                    transition: 'background-color 0.2s ease'
                  }}
                />
              ))}
            </div>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={handleNextQuestion}
                disabled={!canProceed}
                style={{
                  backgroundColor: canProceed ? '#1B365D' : '#F8F9FA',
                  color: canProceed ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: canProceed ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  opacity: canProceed ? 1 : 0.5,
                  boxShadow: canProceed ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleFinishQuiz}
                disabled={(!canProceed && !reviewMode) || isSaving}
                style={{
                  backgroundColor: (reviewMode || canProceed) ? '#1B365D' : '#F8F9FA',
                  color: (reviewMode || canProceed) ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (reviewMode || canProceed) && !isSaving ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (reviewMode || canProceed) && !isSaving ? 1 : 0.5,
                  boxShadow: (reviewMode || canProceed) && !isSaving ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                {isSaving ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #D1D5DB',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Saving...
                  </>
                ) : (
                  reviewMode ? 'Finish Review' : 'Finish Quiz'
                )}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div style={{ 
            textAlign: 'center', 
            padding: '24px 0',
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#ECFDF5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              border: '1px solid #10B981'
            }}>
              <CheckMarkSVG size={24} color="#10B981" />
            </div>
            
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
              Quiz Complete!
            </h3>
            
            <div style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '16px',
              fontWeight: '500'
            }}>
              Score: {score} / {questions.length}
            </div>
            
            <div style={{
              backgroundColor: '#F8F9FA',
              color: '#374151',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              border: '1px solid #E5E7EB'
            }}>
              <CheckMarkSVG size={12} color="#374151" />
              Results saved successfully
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          *, *::before, *::after {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
        `
      }} />
    </div>
  );
};

export default QuizSession00;